<template>
  <div class="conny-page">
    <headerSection v-if="!standalonePreview" />
    <main class="conny-page__main">
      <div class="conny-page__top">
        <router-link to="/" aria-label="กลับหน้าหลัก">←</router-link>
        <div><small>คู่หูนำโชค</small><h1>คู่หูของฉัน</h1></div>
        <button v-if="pet" type="button" @click="openProfile">✎ ชื่อ</button>
      </div>

      <div v-if="loading" class="conny-page__state">กำลังพาคู่หูมาหา...</div>
      <div v-else-if="error" class="conny-page__state conny-page__state--error">
        <strong>{{ error }}</strong><router-link to="/">กลับหน้าหลัก</router-link>
      </div>
      <template v-else-if="pet">
        <div v-if="notice" class="conny-notice">{{ notice }}</div>
        <div class="conny-pet-grid">
          <ConnyAvatar :pet="displayPet" :loading="playing" @play="play" @pet-tap="petTapCount += 1" />
          <aside class="conny-pet-panel">
            <div class="conny-pet-panel__name">
              <span>{{ pet.gender === 'female' ? '♀' : pet.gender === 'male' ? '♂' : '✦' }}</span>
              <div><small>ชื่อคู่หูของคุณ</small><h2>{{ pet.name || 'ยังไม่มีชื่อ' }}</h2></div>
            </div>
            <ConnyProgress :pet="pet" />
            <div class="conny-pet-panel__stats">
              <ConnyStatusStrip icon="🍚" title="ให้อาหาร" :detail="pet.lastFedDate ? `ล่าสุด ${pet.lastFedDate}` : 'ยังไม่เคยให้อาหาร'" />
              <ConnyStatusStrip icon="♡" title="เล่นด้วยกัน" :detail="pet.playedToday ? 'เล่นแล้ววันนี้' : 'รับได้วันละ 5 XP'" />
              <ConnyStatusStrip icon="✨" title="จำนวนครั้งที่ให้อาหาร" :detail="`${pet.feedCount || 0} ครั้ง`" />
            </div>
            <div class="conny-pet-panel__links">
              <router-link :to="previewRoute('/checkin')"><b>เช็กอินให้อาหาร</b><span>ฝากครบ 100 บาท รับ 10 XP และเหรียญ ›</span></router-link>
              <router-link :to="previewRoute('/missions')"><b>ภารกิจ</b><span>ดูเป้าหมายรายวันและรายสัปดาห์ ›</span></router-link>
            </div>
          </aside>
        </div>
        <ConnyEvolutionGallery :pet="pet" />
      </template>
    </main>
    <footerSection v-if="!standalonePreview" />
    <ConnyProfileModal
      :visible="showProfile"
      :pet="pet || {}"
      :saving="saving"
      :error="profileError"
      @close="closeProfile"
      @save="saveProfile"
    />
  </div>
</template>

<script>
import connyApi, { connyErrorMessage, connyPreviewEnabled } from '../services/connyRetention'
import ConnyAvatar from '../components/conny/ConnyAvatar.vue'
import ConnyEvolutionGallery from '../components/conny/ConnyEvolutionGallery.vue'
import ConnyProgress from '../components/conny/ConnyProgress.vue'
import ConnyProfileModal from '../components/conny/ConnyProfileModal.vue'
import ConnyStatusStrip from '../components/conny/ConnyStatusStrip.vue'

export default {
  name: 'ConnyPet',
  components: {
    ConnyAvatar,
    ConnyEvolutionGallery,
    ConnyProgress,
    ConnyProfileModal,
    ConnyStatusStrip,
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  data () {
    return { pet: null, loading: true, playing: false, saving: false, showProfile: false, notice: '', error: '', profileError: '', petTapCount: 0 }
  },
  computed: {
    standalonePreview () {
      return connyPreviewEnabled && this.$route.query.preview === 'standalone'
    },
    moodOverride () {
      const mood = String(this.$route.query.mood || '').toLowerCase()
      return ['new', 'happy', 'content', 'hungry', 'sulking'].indexOf(mood) >= 0 ? mood : ''
    },
    displayPet () {
      if (!this.pet || !this.moodOverride) return this.pet
      return Object.assign({}, this.pet, { mood: this.moodOverride })
    }
  },
  mounted () {
    this.installPreviewTestHooks()
    this.load()
  },
  beforeDestroy () {
    if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') return
    if (window.render_game_to_text === this.renderPreviewState) delete window.render_game_to_text
    if (window.advanceTime === this.advancePreviewTime) delete window.advanceTime
  },
  methods: {
    installPreviewTestHooks () {
      if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') return
      window.render_game_to_text = this.renderPreviewState
      window.advanceTime = this.advancePreviewTime
    },
    renderPreviewState () {
      return JSON.stringify({
        mode: 'conny-pet',
        pet: this.displayPet,
        ui: {
          loading: this.loading,
          playing: this.playing,
          profileOpen: this.showProfile,
          notice: this.notice,
          moodOverride: this.moodOverride,
          petTapCount: this.petTapCount
        }
      })
    },
    advancePreviewTime () { return this.renderPreviewState() },
    async load () {
      this.loading = true
      try {
        const response = await connyApi.getPetStatus()
        this.pet = response.pet
        this.showProfile = !this.pet.name || !this.pet.gender
      } catch (error) {
        this.error = connyErrorMessage(error)
      } finally { this.loading = false }
    },
    async play () {
      this.playing = true
      this.notice = ''
      try {
        const response = await connyApi.play()
        this.pet = response.pet
        const extras = response.coinBonus ? ` และโบนัสเลเวล ${response.coinBonus} เหรียญ` : ''
        this.notice = `เล่นด้วยกันแล้ว รับ 5 XP${extras}`
      } catch (error) { this.notice = connyErrorMessage(error) } finally { this.playing = false }
    },
    requestId () { return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}` },
    previewRoute (path) {
      return this.standalonePreview ? { path, query: { preview: 'standalone' } } : path
    },
    openProfile () {
      this.profileError = ''
      this.showProfile = true
    },
    closeProfile () {
      this.profileError = ''
      this.showProfile = false
    },
    async saveProfile (profile) {
      this.saving = true
      this.notice = ''
      this.profileError = ''
      try {
        const payload = Object.assign({}, profile)
        if (this.pet.name && this.pet.name !== profile.name) payload.requestId = this.requestId()
        const response = await connyApi.updateProfile(payload)
        this.pet = response.pet
        this.showProfile = false
        this.notice = response.coinCharged ? 'เปลี่ยนชื่อสำเร็จ ใช้ 5 เหรียญ' : 'บันทึกคู่หูสำเร็จ'
      } catch (error) {
        this.profileError = connyErrorMessage(error)
      } finally { this.saving = false }
    }
  }
}
</script>

<style scoped>
.conny-page { min-height: 100vh; background: #03051b; font-family: 'Kanit', sans-serif; }
.conny-page__main { width: min(1080px, calc(100% - 28px)); min-height: 76vh; margin: 0 auto; padding: 105px 0 45px; }
.conny-page__top { display: grid; grid-template-columns: 44px 1fr auto; align-items: center; gap: 10px; margin-bottom: 18px; color: #fff; }
.conny-page__top > a { display: grid; width: 40px; height: 40px; place-items: center; border: 1px solid rgba(255,255,255,.1); border-radius: 13px; background: rgba(255,255,255,.06); color: #fff; font-size: 20px; text-decoration: none; }
.conny-page__top small { color: #67dbbd; font-size: 11px; font-weight: 900; letter-spacing: .08em; }
.conny-page__top h1 { margin: 0; font-size: 25px; font-weight: 900; }
.conny-page__top button { min-height: 39px; padding: 0 13px; border: 1px solid rgba(255,218,112,.25); border-radius: 12px; background: rgba(251,200,68,.08); color: #ffe07b; font-family: inherit; font-size: 12px; }
.conny-page__state { min-height: 420px; display: grid; place-items: center; color: rgba(255,255,255,.65); }
.conny-page__state--error { align-content: center; gap: 12px; text-align: center; }
.conny-page__state--error a { color: #ffd85d; }
.conny-notice { margin-bottom: 13px; padding: 11px 14px; border: 1px solid rgba(103,219,189,.22); border-radius: 13px; background: rgba(103,219,189,.09); color: #baf8e7; font-size: 13px; }
.conny-pet-grid { display: grid; grid-template-columns: minmax(0, 1.16fr) minmax(330px, .84fr); gap: 18px; }
.conny-pet-panel { display: flex; flex-direction: column; gap: 13px; padding: 20px; border: 1px solid rgba(255,255,255,.08); border-radius: 25px; background: linear-gradient(160deg, #0e1c49, #070923); }
.conny-pet-panel__name { display: flex; align-items: center; gap: 12px; color: #fff; }
.conny-pet-panel__name > span { display: grid; width: 48px; height: 48px; place-items: center; border-radius: 15px; background: rgba(103,219,189,.12); color: #71e5c7; font-size: 23px; }
.conny-pet-panel__name small { color: rgba(255,255,255,.5); font-size: 11px; }
.conny-pet-panel__name h2 { margin: 2px 0 0; font-size: 22px; }
.conny-pet-panel__stats { display: grid; gap: 8px; }
.conny-pet-panel__links { display: grid; gap: 8px; margin-top: auto; }
.conny-pet-panel__links a { display: block; padding: 12px 13px; border-radius: 13px; background: rgba(255,255,255,.05); color: #fff; text-decoration: none; }
.conny-pet-panel__links b, .conny-pet-panel__links span { display: block; }
.conny-pet-panel__links b { font-size: 13px; }
.conny-pet-panel__links span { margin-top: 2px; color: rgba(255,255,255,.5); font-size: 10px; }
@media (max-width: 820px) { .conny-pet-grid { grid-template-columns: 1fr; } }
@media (max-width: 575px) { .conny-page__main { padding-top: 24px; } .conny-pet-panel { padding: 15px; border-radius: 20px; } }
</style>
